/**
 * Web Push subscription helpers for Rammy.
 *
 * Flow:
 *   1. registerSW() (from pwa.js) installs the service worker.
 *   2. isPushSupported() gates the UI so we never surface anything on unsupported browsers.
 *   3. requestPermissionAndSubscribe() → prompts the browser, subscribes with
 *      the VAPID public key fetched from `/api/push/status`, and persists the
 *      subscription server-side.
 *   4. unsubscribe() removes both the browser sub and the DB row.
 *   5. sendTestPush() fires a test notification through the server.
 */
import api from "@/lib/api";

/** Native browser support check. */
export function isPushSupported() {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function notificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

/** Fetch VAPID public key + current subscription status from the server. */
export async function getPushStatus() {
  const { data } = await api.get("/push/status");
  return data; // { supported, public_key, subscribed, subscription_count }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

async function getReadyRegistration() {
  // Register on-the-fly if not already registered (e.g. dev mode where pwa.js
  // skipped the auto-registration).
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return navigator.serviceWorker.ready;
  await navigator.serviceWorker.register("/service-worker.js");
  return navigator.serviceWorker.ready;
}

/**
 * Ask for permission (if not yet granted) and register a Web Push subscription.
 *
 * Returns `{ ok: true }` on success, `{ ok: false, reason }` on any failure —
 * caller decides how to surface it (toast, inline hint).
 */
export async function requestPermissionAndSubscribe() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return { ok: false, reason: permission };

  try {
    const status = await getPushStatus();
    if (!status?.public_key) return { ok: false, reason: "no-vapid-key" };

    const reg = await getReadyRegistration();

    // If the browser already has a subscription for this SW, reuse it.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(status.public_key),
      });
    }

    const json = sub.toJSON();
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    });

    return { ok: true };
  } catch (err) {
    console.warn("[push] subscribe failed:", err);
    return { ok: false, reason: err?.message || "subscribe-error" };
  }
}

/** Cancel subscription on both browser and server. */
export async function unsubscribe() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || "unsubscribe-error" };
  }
}

/** Ask the server to fire a test push to every subscription this user has. */
export async function sendTestPush() {
  const { data } = await api.post("/push/test", {});
  return data;
}
